// src/components/solution_sections/TechnologyOrchestrationSection.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card } from '@dopomogai/ui';
import { Button } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';

// Define the shape of the data this component expects
interface TechItem {
  name: string;
  description: string;
  icon?: string;
}
interface TechData {
  sectionTitle?: string;
  icon?: string;
  coreTechnologies: TechItem[];
  orchestration: {
    title: string;
    description: string;
  };
  platformFoundation: {
    statement: string;
    buttonText: string;
    platformLinkUrl: string;
  };
}

interface TechnologyOrchestrationSectionProps {
  data: TechData;
  id: string;
  headerBgColor?: string;
  onAction: (actionType: string, payload?: any) => void;
}

const listVariants = {
  visible: { transition: { staggerChildren: 0.1 } },
  hidden: {},
};
const itemVariants = {
  visible: { opacity: 1, x: 0, transition: { duration: 0.4, ease: 'easeOut' } },
  hidden: { opacity: 0, x: -20 },
};
const singleItemVariants = {
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
    hidden: { opacity: 0, y: 20 },
}

// --- THE FIX IS HERE ---
// Added the missing closing `}) =>` after the props destructuring
export const TechnologyOrchestrationSection = React.forwardRef<HTMLDivElement, TechnologyOrchestrationSectionProps>(({
  data,
  id,
  headerBgColor,
  onAction,
}, ref) => {
// --- END OF FIX ---
  const { t } = useTranslation('solution_showcase');

  if (!data) return null;

  return (
    <Card
      ref={ref}
      title={data.sectionTitle || t('technology.defaultTitle')} // Use data title or fallback to generic i18n
      icon={data.icon || 'cpu'}
      id={id}
      headerBgColor={headerBgColor}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
        
        {data.coreTechnologies && data.coreTechnologies.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">{t('technology.coreTechTitle')}</h3>
            <motion.div
              variants={listVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="space-y-4"
            >
              {data.coreTechnologies.map((tech, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -2, x: -2, boxShadow: "2px 2px 0px 0px rgb(17,24,39)" }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg border-2 border-black"
                >
                  <div className="flex-shrink-0 text-indigo-600 mt-1">
                    {renderIcon(tech.icon || 'wrench', { size: 24 })}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{tech.name}</h4>
                    {tech.description && (
                      <p className="text-sm text-gray-600 mt-1">{tech.description}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        )}

        <div className="space-y-8">
          {data.orchestration && (
            <motion.div
                variants={singleItemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-4">{data.orchestration.title}</h3>
              <motion.div
                initial={{ boxShadow: 'none' }}
                whileHover={{ y: -4, x: -4, boxShadow: '8px 8px 0px 0px rgb(17,24,39)' }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="bg-white p-5 rounded-xl border-2 border-black"
              >
                 <p className="text-gray-700 leading-relaxed">
                    {data.orchestration.description}
                 </p>
              </motion.div>
            </motion.div>
          )}

          {data.platformFoundation && (
             <motion.div
              variants={singleItemVariants}
              transition={{ delay: 0.1 }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
              whileHover={{ y: -4, x: -4, boxShadow: '8px 8px 0px 0px #4338ca' }}
              className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border-2 border-black text-center"
            >
              <p className="text-indigo-900 mb-4 font-medium">{data.platformFoundation.statement}</p>
              {data.platformFoundation.platformLinkUrl && (
                <Button
                  text={data.platformFoundation.buttonText}
                  variant="secondary"
                  size="sm"
                  icon={<ExternalLink size={14} />}
                  iconPosition="right"
                  onClick={() => onAction('OPEN_URL', { url: data.platformFoundation.platformLinkUrl })}
                />
              )}
            </motion.div>
          )}
        </div>
      </div>
    </Card>
  );
});

